/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Global } from '@emotion/react'
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons'
import {
  DatePicker as AntDatePicker,
  Switch,
  TimePicker as AntTimePicker,
  Tooltip,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { GameFormValues } from '@sportninja/common/features/game/gameForm'
import {
  paragraphSmall,
  paragraphXSmall,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'

// Same picker recipe as the registration setup general form
// (`RegistrationWindowFields`), so date/time entry behaves identically here.
//
// `type: 'mask'` gives a guided input: the placeholder stays in place and the
// caret steps through the segments as the admin types.
const DATE_MASK = { format: 'MM/DD/YYYY', type: 'mask' } as const

// Deliberately NOT masked. rc-picker's MaskFormat only treats
// ['YYYY','MM','DD','HH','mm','ss','SSS'] as editable cells, so under a mask a
// 12-hour `hh:mm A` exposes only the minutes -- the hours and the AM/PM marker
// are not cells and cannot be typed.
const TIME_FORMAT = 'hh:mm A'

// `.utc()` is used to read the TBD sentinel's true (UTC) date. Registered here
// rather than relying on another module in the bundle having done it first.
dayjs.extend(utcPlugin)

const TIME_POPUP_CLASS = 'game-modal-time-popup'

interface Props {
  values: GameFormValues
  onChange: (next: Partial<GameFormValues>) => void
  disabled?: boolean
  hasError?: (name: keyof GameFormValues) => boolean
  /** API error messages, keyed by form field name. */
  fieldErrors?: Record<string, string>
}

/**
 * Start date, start time and duration on one row, with the Time TBD switch
 * riding the Start Time label.
 *
 * TBD keeps the legacy semantics exactly: form state always holds the REAL
 * picked time, and the 23:58:58 UTC sentinel is applied on the way out in
 * `buildGamePayload`. That is what lets the toggle be turned back off without
 * having lost the time the admin chose. While it is on, the time field stays
 * visible but disabled rather than hiding, so the row never reflows.
 */
const GameDateTimeFields: React.FC<Props> = ({
  values,
  onChange,
  disabled = false,
  hasError,
  fieldErrors = {},
}) => {
  const current = values.startsAt ? dayjs(values.startsAt) : null

  const handleDate = (next: Dayjs | null) => {
    if (!next) {
      onChange({ startsAt: '' })
      return
    }
    // Keep the already-picked time-of-day when only the date changes; on the
    // first pick default to the current time rather than midnight, which is
    // almost never the intended kickoff.
    const source = current ?? dayjs()
    const merged = next
      .hour(source.hour())
      .minute(source.minute())
      .second(0)
      .millisecond(0)
    onChange({ startsAt: merged.toISOString() })
  }

  const handleTime = (next: Dayjs | null) => {
    if (!next) {
      return
    }
    // The field is disabled until a date exists, so `current` is normally set;
    // falling back to today keeps a stray change from being dropped silently.
    onChange({
      startsAt: (current ?? dayjs())
        .hour(next.hour())
        .minute(next.minute())
        .second(0)
        .millisecond(0)
        .toISOString(),
    })
  }

  const handleDuration = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    onChange({ durationMinutes: raw === '' ? null : Number(raw) })
  }

  /**
   * Toggling TBD OFF pre-selects the current time of day, keeping the chosen
   * date.
   *
   * A game saved as TBD has no real time to restore -- its stored start is the
   * 23:58:58 UTC sentinel. Leaving that in place meant switching the toggle off
   * and pressing Confirm re-sent the sentinel, so the game stayed TBD unless
   * the user also changed the time by hand. Moving to "now" both gives a
   * sensible default and takes the value off the sentinel, so it stops reading
   * as TBD. Mirrors the legacy `handleTimeTDBChange`.
   */
  const handleTbdChange = (checked: boolean) => {
    if (checked) {
      onChange({ timeToBeDefined: true })
      return
    }

    const now = dayjs()

    /*
     * Take the DAY from the sentinel in UTC, not local time.
     *
     * `setTimeToBeDefined` stamps 23:58:58 *UTC*, so in any zone ahead of UTC
     * that instant lands on the following local day (23:58 UTC on Apr 2 is
     * 08:58 on Apr 3 in Tokyo). Reading the date locally would therefore push
     * the game forward a day the moment TBD is switched off.
     */
    const base = current
      ? dayjs()
          .year(current.utc().year())
          .month(current.utc().month())
          .date(current.utc().date())
      : now

    const restored = base
      .hour(now.hour())
      .minute(now.minute())
      .second(0)
      .millisecond(0)

    onChange({ timeToBeDefined: false, startsAt: restored.toISOString() })
  }

  const popupContainer = (triggerNode: HTMLElement) =>
    triggerNode.parentNode as HTMLElement

  const startError = fieldErrors.startsAt
  const durationError = fieldErrors.durationMinutes

  return (
    <div css={groupStyle}>
      <div css={rowStyle}>
        {/* antd renders the time columns in a portal, so the selected cell is
            styled through a popup class rather than the trigger's css prop. */}
        <Global styles={timePopupStyles} />

        <div css={dateFieldStyle}>
          <span css={labelStyle}>
            {capitalizeWords('Start Date')}{' '}
            <span css={requiredMarkStyle}>*</span>
          </span>
          <AntDatePicker
            value={current}
            onChange={handleDate}
            format={DATE_MASK}
            disabled={disabled}
            status={hasError?.('startsAt') ? 'error' : ''}
            placeholder='MM/DD/YYYY'
            css={controlStyle}
            suffixIcon={<CalendarOutlined css={iconStyle} />}
            getPopupContainer={popupContainer}
          />
        </div>

        <div css={timeFieldStyle}>
          <span css={[labelStyle, splitLabelStyle]}>
            <span>
              {capitalizeWords('Start Time')}{' '}
              <span css={requiredMarkStyle}>*</span>
            </span>
            <Tooltip title='Time To Be Defined' zIndex={9999}>
              <span css={tbdStyle}>
                <span
                  css={[tbdTextStyle, values.timeToBeDefined && tbdTextOnStyle]}
                >
                  TBD
                </span>
                <Switch
                  size='small'
                  checked={values.timeToBeDefined}
                  disabled={disabled}
                  onChange={handleTbdChange}
                  aria-label='Time to be defined'
                />
              </span>
            </Tooltip>
          </span>
          <AntTimePicker
            value={values.timeToBeDefined ? null : current}
            onChange={handleTime}
            format={TIME_FORMAT}
            use12Hours
            needConfirm={false}
            /*
             * Quarter-hour options only -- games are scheduled on :00/:15/:30/:45
             * far more often than not, and a 60-row minute column makes the
             * common case a scroll.
             *
             * This limits the LIST, not the value: the input stays free text, so
             * an odd time is still typed straight in ("9:42 AM") and an existing
             * off-step time round-trips untouched.
             */
            minuteStep={15}
            // Disabled while TBD is on: the published time is the sentinel, so
            // letting it be edited would imply a precision that is not stored.
            disabled={disabled || values.timeToBeDefined || !values.startsAt}
            status={hasError?.('startsAt') ? 'error' : ''}
            placeholder={values.timeToBeDefined ? 'TBD' : 'hh:mm AM'}
            css={controlStyle}
            suffixIcon={<ClockCircleOutlined css={iconStyle} />}
            popupClassName={TIME_POPUP_CLASS}
            getPopupContainer={popupContainer}
          />
        </div>

        <div css={durationFieldStyle}>
          <span css={labelStyle}>
            {capitalizeWords(t('common:duration', {}))}{' '}
            <span css={requiredMarkStyle}>*</span>
          </span>
          <div
            css={[
              durationWrapperStyle,
              hasError?.('durationMinutes') && durationErrorStyle,
              disabled && durationDisabledStyle,
            ]}
          >
            <input
              type='number'
              min={0}
              step={1}
              value={values.durationMinutes ?? ''}
              placeholder='e.g. 60'
              onChange={handleDuration}
              disabled={disabled}
              css={durationInputStyle}
              aria-label='Duration in minutes'
            />
            <span css={durationAddonStyle}>{t('common:minutes', {})}</span>
          </div>
          {durationError && <span css={errorStyle}>{durationError}</span>}
        </div>
      </div>
      {/* Only the start error sits under the whole row: date and time share one
          instant, so an API rejection of `starts_at` is not attributable to
          just one of them. Duration owns its own slot, under its own field. */}
      {startError && <span css={errorStyle}>{startError}</span>}
    </div>
  )
}

export default GameDateTimeFields

// ─── Styles ────────────────────────────────────────────────────────────────

const groupStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const errorStyle = css`
  ${paragraphXSmall}
  color: ${colors.ERROR_LIGHT};
`

const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`

const fieldBase = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  @media (max-width: 640px) {
    width: 100%;
  }
`

const dateFieldStyle = css`
  ${fieldBase}
  flex: 1 1 0;
`

const timeFieldStyle = css`
  ${fieldBase}
  flex: 1 1 0;
`

const durationFieldStyle = css`
  ${fieldBase}
  flex: 1 1 0;
`

const labelStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.SOLITUDE};
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 20px;
`

const splitLabelStyle = css`
  justify-content: space-between;
  gap: 8px;
`

const requiredMarkStyle = css`
  color: ${colors.WHITE};
`

const tbdStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const tbdTextStyle = css`
  ${weightSemiBold}
  font-size: 11px;
  line-height: 16px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
`

const tbdTextOnStyle = css`
  color: ${colors.WHITE};
`

// Matches the shared DatePicker chrome so these sit in the form exactly like
// every other 40px control.
const controlStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  height: 40px !important;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};
`

const iconStyle = css`
  color: ${colors.WHITE};
  font-size: 16px;
`

const durationWrapperStyle = css`
  display: flex;
  align-items: center;
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};
  overflow: hidden;
`

const durationErrorStyle = css`
  border-color: ${colors.ERROR_LIGHT};
`

const durationDisabledStyle = css`
  opacity: 0.45;
`

const durationInputStyle = css`
  &::placeholder {
    color: ${colors.NEUTRAL_100};
  }

  ${paragraphSmall}
  ${weightRegular}
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 8px 0 12px;
  border: none;
  outline: none;
  background: transparent;
  color: ${colors.WHITE};

  /* The addon already says "Min"; the spinners only add noise at this width. */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
  appearance: textfield;
  -moz-appearance: textfield;
`

const durationAddonStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  align-self: stretch;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-left: 1px solid ${colors.SECONDARY_100};
  color: ${colors.NEUTRAL_50};
  white-space: nowrap;
`

// DEFAULT_FLAIR is the same token the theme feeds antd as `colorPrimary`, so
// the selected time cell matches the selected day in the calendar.
const timePopupStyles = css`
  .${TIME_POPUP_CLASS}
    .ant-picker-time-panel-column
    > li.ant-picker-time-panel-cell-selected
    .ant-picker-time-panel-cell-inner {
    background: ${colors.DEFAULT_FLAIR};
    color: ${colors.WHITE};

    &:hover {
      background: ${colors.DEFAULT_FLAIR};
      color: ${colors.WHITE};
    }
  }
`
